import { AssistantMarkdownEditPreview } from "@/components/assistant/AssistantMarkdownEditPreview";
import { InstructionWordLimit } from "@/components/form/InstructionWordLimit";
import type { InstructionSummarizeKind } from "@/lib/instruction-limit";
import type { WriteApprovalPlanDraft } from "@/components/assistant/writeApprovalEditablePlanFields";
import { getEditablePlanFieldDefs } from "@/components/assistant/writeApprovalEditablePlanFields";
import type { WriteApprovalEntityKind } from "@/components/assistant/formatWriteApprovalSummary";
import {
  SelfControlledInput,
  SelfControlledTextarea,
} from "@/components/assistant/SelfControlledFieldInputs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

type Props = {
  kind: WriteApprovalEntityKind;
  draft: WriteApprovalPlanDraft;
  onDraftChange: (draft: WriteApprovalPlanDraft) => void;
  disabled?: boolean;
  assistantProposalConflict?: boolean;
  onApplyAssistantProposal?: () => void;
  onKeepLocalEdits?: () => void;
  instructionSourceText?: string;
  onInstructionSummarized?: (summary: string) => void;
  referenceDocuments?: Array<{ id: string; fileName: string; extractedText: string }>;
};

export function AssistantEditableWriteApprovalPlanForm({
  kind,
  draft,
  onDraftChange,
  disabled = false,
  assistantProposalConflict = false,
  onApplyAssistantProposal,
  onKeepLocalEdits,
  instructionSourceText,
  onInstructionSummarized,
  referenceDocuments = [],
}: Props) {
  const fields = getEditablePlanFieldDefs(kind);
  const instructionKind: InstructionSummarizeKind | null =
    kind === "custom-role" ? "review-role" : kind === "draft-legal-role" ? "draft-role" : null;

  const patch = (key: string, value: string) => {
    onDraftChange({ ...draft, [key]: value });
  };

  return (
    <div className="space-y-5">
      {assistantProposalConflict ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-sm text-[#92400e] dark:text-amber-200">
          <p className="font-medium">The assistant sent an updated proposal.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button type="button" size="xs" variant="surface" onClick={onApplyAssistantProposal}>
              Use assistant version
            </Button>
            <Button type="button" size="xs" variant="ghost" onClick={onKeepLocalEdits}>
              Keep my edits
            </Button>
          </div>
        </div>
      ) : null}

      <section className="space-y-3">
        <h4 className="text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
          Proposal fields
        </h4>
        <div className="space-y-3">
          {fields.map((field) => {
            const value = draft[field.key] ?? "";
            if (field.type === "markdown") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <AssistantMarkdownEditPreview
                    label={field.label}
                    value={value}
                    onChange={(next) => patch(field.key, next)}
                    disabled={disabled}
                  />
                  {instructionKind && field.key === "instructions" ? (
                    <InstructionWordLimit
                      text={instructionSourceText ?? value}
                      kind={instructionKind}
                      disabled={disabled}
                      onSummarized={(summary) => {
                        if (onInstructionSummarized) {
                          onInstructionSummarized(summary);
                          return;
                        }
                        patch(field.key, summary);
                      }}
                    />
                  ) : null}
                </div>
              );
            }
            if (field.type === "code" || field.type === "textarea") {
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={`plan-editable-${field.key}`}>{field.label}</Label>
                  <SelfControlledTextarea
                    id={`plan-editable-${field.key}`}
                    value={value}
                    disabled={disabled}
                    rows={field.type === "code" ? 12 : 4}
                    className={field.type === "code" ? "font-mono text-xs" : undefined}
                    onValueChange={(next) => patch(field.key, next)}
                  />
                </div>
              );
            }
            return (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={`plan-editable-${field.key}`}>{field.label}</Label>
                <SelfControlledInput
                  id={`plan-editable-${field.key}`}
                  value={value}
                  disabled={disabled}
                  onValueChange={(next) => patch(field.key, next)}
                />
              </div>
            );
          })}
        </div>
      </section>

      {referenceDocuments.length > 0 ? (
        <section className="space-y-2">
          <h4 className="text-[11px] font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
            Reference PDFs
          </h4>
          <ul className="space-y-2">
            {referenceDocuments.map((doc) => (
              <li
                key={doc.id}
                className="rounded-md border border-[rgba(15,23,42,0.09)] px-2 py-1.5 text-xs dark:border-white/10"
              >
                <p className="truncate">{doc.fileName}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
